import "shared/extensions/date";

import { Content, DateOnly, LeaderboardParticipant, useLeaveLeaderboardFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { getNow, getTimeLeft, millisecondsInADay } from "core/helpers/time";
import { useRef, useState } from "react";
import { Hideable } from "shared/UI/Hideable/Hideable";
import { InformationModal } from "./components/InformationModal/InformationModal";
import { InviteModal } from "./components/InviteModal/InviteModal";
import { LeaveModal } from "./components/LeaveModal/LeaveModal";
import { LooseObject } from "shared/core/LooseObject";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import { ReactComponent as RightChevron } from "shared/assets/Icons/right.svg";
import Button from "shared/UI/Buttons/Button";
import { UserState } from "core/state/userState";
import classNames from "classnames";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { NarrowContainer } from "shared/UI/LayoutComponents/LayoutComponents";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";

type LeaderboardParticipantWithPosition = LeaderboardParticipant & { position: string };

export type LeaderboardDisplayProps = {
  id: string;
  name: string;
  endDate: DateOnly;
  participants: LeaderboardParticipant[];
  description: Content | undefined;
  isTemplateLoading: boolean;
  code: string;
};

export const LeaderboardDisplay = ({ name, endDate, participants, description, isTemplateLoading, code }: LeaderboardDisplayProps) => {
  const { modalNotify } = useNotifyModalContext();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const navigate = useNavigate();
  const { trigger } = useLeaveLeaderboardFunctionRequest({});

  const currentUserPlace = useRef<HTMLTableRowElement | undefined>();
  const numberOfMembers = t("leaderboards.display.subTitle", { count: participants.length });
  const timeLeft = getTimeLeft(getNow(), Date.fromDateOnly(endDate)!);

  const isLeaderboardFinished = getNow().getTime() >= Date.fromDateOnly(endDate)!.getTime() + millisecondsInADay;

  const displayParticipants = getParticipantsWithPosition(participants);

  const currentUserId = UserState.get()?.id;
  const currentUser = displayParticipants.find((participant) => participant.id === currentUserId);

  const PositionNote = () => {
    const placementLabel = isLeaderboardFinished
      ? t("leaderboards.display.finalPlaceLabel", { place: currentUser!.position })
      : t("leaderboards.display.currentPlaceLabel", { place: currentUser!.position });

    return (
      <button className="w-full mb-4" onClick={() => currentUserPlace?.current?.scrollIntoView?.({ block: "center" })}>
        <div className="flex flex-row w-full">
          <div className="flex-grow" role="note">
            {placementLabel}
          </div>
          <div className="flex items-center">
            <RightChevron className="mt-0 text-grey" />
          </div>
        </div>
      </button>
    );
  };

  const leaderboardActionMenu = [
    {
      title: t("leaderboards.display.informationButton"),
      onClick: () => setShowInfoModal(true),
    },
    {
      title: t("leaderboards.display.leaveButton"),
      onClick: () => setShowLeaveModal(true),
    },
  ];

  async function leaveLeaderboard() {
    try {
      await trigger({ body: { leaderboardCode: code! } });
      navigate("/leaderboards");
    } catch {
      modalNotify("error", t("leaderboards.leave.error"));
      setShowLeaveModal(false);
    }
  }

  return (
    <>
      <PageHeader
        narrowView
        title={name}
        subtitle={`${numberOfMembers} - ${timeLeft}`}
        contextMenuList={!isLeaderboardFinished ? leaderboardActionMenu : undefined}
      />
      <NarrowContainer>
        <div className="flex flex-col flex-grow h-full">
          <div className="flex-grow">
            <PositionNote />
            <table className="w-full scores-table">
              <thead>
                <tr>
                  <th>{t("leaderboards.display.tableHeadings.position")}</th>
                  <th>{t("leaderboards.display.tableHeadings.name")}</th>
                  <th>{t("leaderboards.display.tableHeadings.score")}</th>
                </tr>
              </thead>
              <tbody>
                {displayParticipants.map((participant: any) => {
                  const isCurrentUserRow = participant.id === currentUserId;

                  const rowProps: LooseObject = isCurrentUserRow ? { ref: currentUserPlace } : {};
                  rowProps["key"] = participant.id;

                  return (
                    <tr {...rowProps} className={classNames({ userRow: isCurrentUserRow })}>
                      <td>{participant.position}</td>
                      <td>{participant.name}</td>
                      <td>{participant.score}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Hideable hidden={isLeaderboardFinished}>
            <Button className="mt-6" text={t("leaderboards.display.inviteButton")} onClick={() => setShowInviteModal(true)} />
          </Hideable>
          <Hideable hidden={!showInfoModal}>
            <InformationModal isTemplateLoading={isTemplateLoading} title={name} description={description} onClose={() => setShowInfoModal(false)} />
          </Hideable>
          <Hideable hidden={!showLeaveModal}>
            <LeaveModal onConfirm={() => leaveLeaderboard()} onClose={() => setShowLeaveModal(false)} />
          </Hideable>
          <Hideable hidden={!showInviteModal}>
            <InviteModal
              code={code}
              onClose={() => {
                setShowInviteModal(false);
              }}
            />
          </Hideable>
        </div>
      </NarrowContainer>
    </>
  );
};

function sortParticipants(participants: LeaderboardParticipant[]) {
  return participants.sort((a, b) => (a.score < b.score ? 1 : -1));
}

function withPosition(participants: LeaderboardParticipant[]) {
  const result: LeaderboardParticipantWithPosition[] = [];
  let lastScore: number | undefined = undefined;
  let lastPosition: string | undefined = undefined;

  for (let i = 0; i < participants.length; ++i) {
    let position = (i + 1).asOrdinal();
    if (participants[i].score !== lastScore) {
      lastScore = participants[i].score;
      lastPosition = position;
    }

    result.push({ id: participants[i].id, name: participants[i].name, score: lastScore, position: lastPosition! });
  }
  return result;
}

function getParticipantsWithPosition(participants: LeaderboardParticipant[]): LeaderboardParticipantWithPosition[] {
  const participantsInDescendingScoreOrder = sortParticipants(participants);
  return withPosition(participantsInDescendingScoreOrder);
}
